'use client';
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Divider } from '@bgzt/ui';
import { SearchFilterState, SearchFilterKey } from '~/types';
import {
  useAppDidFavorite,
  useSearchFilterSearchParamsClient,
  useStoredSearchFilters,
  useUpdateInfiniteQueryProductFavorite,
} from '~/hooks';

import { searchShopProductsInfiniteQueryOptions } from '~/queries/search';
import { FilterValue, ProductDataV2, SearchShopProductsParams, SearchSortEnum } from '~/apis/search';

import {
  FilterBubble,
  Section,
  Spacing,
  getIsHighlighted,
  getSelectedKeys,
  useOverlay,
  getFilterBubbleFormattedText,
  SortFilter,
  FilterBottomSheet,
  LinkByEnvironment,
  ProductItem,
  ProductListTemp,
  EmptyMessage,
  LogVisit,
  LogImpression,
  LogClick,
} from '~/components';
import { ShopSearchForm, ShopDescription, ShopInfo, ShopBanner } from './_components';

import { EVENT_TYPE, CONTENT_TYPE, EVENT_ACTION, PAGE_ID, VIEW_ID } from '~/apis/log/constant';
import { getShopInfo } from '~/utils/shop-service';
import { ShopId } from '~/constants';

type Props = {
  initialPageParam: SearchShopProductsParams;
};
export const ShopDetail = ({ initialPageParam }: Props) => {
  const overlay = useOverlay();

  const { searchParams, setSearchParams, hasSearchFilter } = useSearchFilterSearchParamsClient();

  const mergedSearchParams = {
    ...initialPageParam,
    ...(searchParams as SearchShopProductsParams),
    q: searchParams.q || undefined,
  };

  const queryOptions = searchShopProductsInfiniteQueryOptions(mergedSearchParams, {
    staleTime: 3000,
  });

  const {
    data: searchProductsData,
    fetchNextPage,
    hasNextPage,
    isFetched,
    isFetchingNextPage,
  } = useInfiniteQuery(queryOptions);

  const { updateInfiniteQueryProductFavorite } = useUpdateInfiniteQueryProductFavorite();
  useAppDidFavorite((pid, type) => {
    updateInfiniteQueryProductFavorite({
      queryKey: queryOptions.queryKey,
      pid: Number(pid),
      isFavorite: type === 'add',
    });
  });

  const searchFilters = useStoredSearchFilters(searchProductsData?.filters);

  const selectedKeys = useMemo(() => {
    return getSelectedKeys(searchParams, searchFilters);
  }, [searchParams, searchFilters]);

  const isHighlighted = useMemo(() => {
    return getIsHighlighted(selectedKeys.keys, searchFilters);
  }, [selectedKeys, searchFilters]);

  const handleSubmitFilter = (filter: SearchFilterState) => {
    const params = { ...filter, categoryId: filter.categoryId ? [filter?.categoryId] : null };
    setSearchParams(params);
  };

  const handleSortChange = (value?: string) => {
    const params = { sort: value as SearchSortEnum };
    setSearchParams(params);
  };

  const handleSearchFormSubmit = (value: string) => {
    const params = { ...(searchParams as SearchShopProductsParams), q: value };
    setSearchParams(params);
  };
  const handleSearhFormCancel = () => {
    const params = { ...(searchParams as SearchShopProductsParams), q: null };
    setSearchParams(params);
  };

  const handleMoreViewClick = async () => {
    await fetchNextPage();
  };

  const openFilterBottomSheet = (scrollTarget: string = '') => {
    overlay.open(({ isOpen, close }) => (
      <FilterBottomSheet
        isOpen={isOpen}
        onClose={() => {
          close();
        }}
        onSubmit={handleSubmitFilter}
        data={searchParams}
        scrollTarget={scrollTarget}
        filters={searchFilters ?? []}
      />
    ));
  };

  const productData = searchProductsData?.pages as ProductDataV2[];
  const hasProductData = productData?.length > 0;
  const sortFilter = searchFilters?.find((filter) => filter.id === 'sort')?.values || [];
  const selectedSortValue = searchParams.sort ?? sortFilter.find((data: FilterValue) => data._default)?.value ?? 'date';

  return (
    <>
      <LogVisit
        params={{
          event_type: EVENT_TYPE.VISIT,
          event_action: EVENT_ACTION.VISIT,
          page_id: PAGE_ID.SHOP,
          page_label: initialPageParam.shopId,
        }}
      />
      <ShopBanner shopId={initialPageParam.shopId} />
      <Spacing height={16} />
      <Section>
        <ShopInfo shopId={initialPageParam.shopId} />
      </Section>
      <Spacing height={16} />
      <Section fullWidth>
        <ShopDescription shopId={initialPageParam.shopId} />
      </Section>

      <Divider thickness={10} />

      {searchFilters && (
        <FilterBubble
          data={searchFilters
            ?.filter(({ shortcut }) => shortcut)
            .map(({ key, name, id }) => {
              if (id === 'price') {
                return {
                  key: 'price',
                  placeholderLabel: name,
                  onClick: () => {
                    openFilterBottomSheet('price');
                  },
                  renderLabel: (value) =>
                    getFilterBubbleFormattedText(id, [value[0] ?? '', value[1] ?? ''], searchFilters ?? []),
                  value: [searchParams.minPrice ?? '', searchParams.maxPrice ?? ''],
                  showsPlaceholder: !searchParams?.minPrice && !searchParams?.maxPrice,
                };
              }

              if (key === 'modelId' || key === 'brandId') {
                return {
                  key,
                  placeholderLabel: name,
                  onClick: () => {
                    openFilterBottomSheet(key);
                  },
                  renderLabel: (value) => getFilterBubbleFormattedText(key, value, searchFilters ?? []),
                  value: searchParams?.brandId ?? [],
                  showsPlaceholder: searchParams?.brandId?.length === 0,
                };
              }

              return {
                key: key ?? id,
                placeholderLabel: name,
                onClick: () => {
                  openFilterBottomSheet(key ?? id);
                },
                renderLabel: (value) => getFilterBubbleFormattedText(key ?? id, value, searchFilters ?? []),
                value: (key ?? id) in searchParams ? [searchParams[(key ?? id) as SearchFilterKey]] : [],
              };
            })}
          selectedKeyShortcutKeys={selectedKeys.shortcutKeys}
          onFilterIconClick={openFilterBottomSheet}
          isHighlighted={isHighlighted}
        />
      )}
      <Spacing height={8} />
      <Section>
        <ShopSearchForm
          defaultValue={searchParams.q}
          onSubmit={handleSearchFormSubmit}
          onCancel={handleSearhFormCancel}
          shopName={getShopInfo(initialPageParam.shopId as ShopId)?.shopNameKor}
        />
      </Section>

      <Spacing height={16} />
      <SortFilter
        totalAmount={searchProductsData?.totalElements ?? 0}
        sortData={sortFilter}
        selectedValue={selectedSortValue}
        onSortChange={handleSortChange}
      />

      <EmptyMessage
        display={Boolean(isFetched && searchProductsData && searchProductsData?.pages?.length < 1)}
        q={searchParams?.q ?? ''}
        hasFilter={hasSearchFilter}
        guide={searchProductsData?.guide}
        isBlockedKeyword={searchProductsData?.responseCode === 'BLOCKED_KEYWORD'}
      />

      <Spacing height={16} />
      <Section>
        <ProductListTemp col={3}>
          {searchProductsData?.pages?.map((item, index) => {
            const product = item as ProductDataV2;
            const path = `/products/${product.pid}`;

            const logParams = {
              page_id: PAGE_ID.SHOP,
              view_id: VIEW_ID.PRODUCT,
              page_label: initialPageParam.shopId,
              content_id: String(product.pid),
              content_position: index,
              content_type: CONTENT_TYPE.MERCARI,
              tracking: product?.tracking,
            };

            return (
              <li key={product.pid}>
                <LogImpression
                  params={{
                    ...logParams,
                    event_type: EVENT_TYPE.IMPRESSION,
                    event_action: EVENT_ACTION.IMPRESSION,
                  }}
                >
                  <LogClick
                    params={{
                      ...logParams,
                      event_type: EVENT_TYPE.SELECT_PRODUCT,
                      event_action: EVENT_ACTION.SELECT_PRODUCT,
                    }}
                  >
                    <LinkByEnvironment href={path} target={'_blank'}>
                      <ProductItem
                        pid={product.pid}
                        image={product.productImage}
                        name={product.name}
                        location={product.location}
                        updatedBefore={product.updatedBefore}
                        price={product.price}
                        status={product.status}
                        faved={product.faved}
                        discountRate={product.discount?.rate}
                        viewType="3-column"
                      />
                    </LinkByEnvironment>
                  </LogClick>
                </LogImpression>
              </li>
            );
          })}
        </ProductListTemp>
      </Section>

      {hasProductData && hasNextPage && (
        <Section>
          <Spacing height={20} />
          <Button
            variant="normalGray"
            size="large"
            width={'100%'}
            onClick={handleMoreViewClick}
            isLoading={isFetchingNextPage}
          >
            더보기
          </Button>
        </Section>
      )}

      <Spacing height={40} />
    </>
  );
};
